/** Personal MCP tokens: the choices, the checks and the words the Access tokens section shows. Pure. */
import { ago } from './format'
import type { PersonalToken } from './schemas'

/** How long a new token lasts. The API takes 1 to 365 days, or null for never; 90 is its default too. */
export const EXPIRY_CHOICES: { value: string; days: number | null; label: string }[] = [
  { value: '30', days: 30, label: '30 days' },
  { value: '90', days: 90, label: '90 days' },
  { value: '365', days: 365, label: 'A year' },
  { value: 'never', days: null, label: 'Never' },
]
export const DEFAULT_EXPIRY = '90'

export function expiryDays(value: string): number | null {
  const c = EXPIRY_CHOICES.find((x) => x.value === value)
  return c ? c.days : 90
}

/** What's wrong with a token name, as the API would say it, or null. */
export function tokenNameProblem(name: string): string | null {
  const n = name.trim()
  if (!n) return 'Name it after where it goes, like "Claude Code on my laptop".'
  if (n.length > 60) return '60 characters at most.'
  // Control characters, as the API refuses them.
  if (/[\u0000-\u001f\u007f]/.test(n)) return 'One line, please.'
  return null
}

const DAY = 86_400_000

export function expiryWords(t: Pick<PersonalToken, 'expiresAt'>, now = Date.now()): string {
  if (!t.expiresAt) return 'never expires'
  const ms = Date.parse(t.expiresAt) - now
  if (ms <= 0) return `expired ${ago(t.expiresAt, now)}`
  // Rounded: a token made a moment ago for 365 days reads 365, not 364 or 366.
  const days = Math.round(ms / DAY)
  return days < 1 ? 'expires within a day' : days === 1 ? 'expires in a day' : `expires in ${days} days`
}

export const lastUsedWords = (t: Pick<PersonalToken, 'lastUsedAt'>, now = Date.now()): string =>
  t.lastUsedAt ? `used ${ago(t.lastUsedAt, now)}` : 'not used yet'

/** The one line under a new token: where it goes. */
export function pasteHint(mcpUrl: string | null): string {
  return mcpUrl
    ? `Paste this into your MCP client as the header "Authorization: Bearer <token>", with the server address ${mcpUrl}.`
    : 'Paste this into your MCP client as the header "Authorization: Bearer <token>". Your app\'s address appears here once it has one.'
}
